import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { memo, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { bool, func, instanceOf, object, oneOf, shape, string } from 'prop-types';
import { ChevronNormalLeft, ChevronNormalRight } from "../../icons";
import { getDaysInMonth, getMonthTimestamp, getYearTimestamp } from "../../utils/date";
import { getLocale } from "../../utils/date/locale";
import Select from '../../Select';
import styles from '../Datepicker.css';
import { initializeYears } from '../helpers';
import { disabledDaysPropType, navbarTypes, themes } from '../propsDefinition';
var cx = classNames.bind(styles);

var checkIsDisabled = function checkIsDisabled(map, value) {
  if (map.min > value.max || map.max < value.min || map.ranges && map.ranges.find(function (range) {
    return range.from < value.min && range.to > value.max;
  })) {
    return true;
  }

  return false;
};

var getDisabledMinAndMaxTimestam = function getDisabledMinAndMaxTimestam(map) {
  var minDate = map.min && new Date(map.min).setDate(0);
  var maxDate;

  if (map.max) {
    var date = new Date(map.max);
    maxDate = date.setDate(getDaysInMonth(date.getFullYear(), date.getMonth()));
  }

  return {
    minDate: minDate,
    maxDate: maxDate
  };
};

var downcaseOption = function downcaseOption(option) {
  return _objectSpread(_objectSpread({}, option), {}, {
    label: option.label.toLowerCase()
  });
};

var createMonths = function createMonths(_ref) {
  var currentYear = _ref.currentYear,
      disabledDaysTimestampMap = _ref.disabledDaysTimestampMap,
      downcaseMonth = _ref.downcaseMonth,
      monthOptions = _ref.monthOptions;
  return monthOptions.map(function (option) {
    var preparedOption = downcaseMonth ? downcaseOption(option) : option;
    var monthTimestamp = currentYear && getMonthTimestamp(currentYear.value, option.value);

    if (monthTimestamp && checkIsDisabled(disabledDaysTimestampMap, monthTimestamp)) {
      return _objectSpread(_objectSpread({}, preparedOption), {}, {
        isDisabled: true
      });
    }

    return preparedOption;
  });
};

var createYears = function createYears(disabledDaysTimestampMap) {
  return initializeYears().map(function (option) {
    var yearTimestamp = getYearTimestamp(option.value);

    if (checkIsDisabled(disabledDaysTimestampMap, yearTimestamp)) {
      return _objectSpread(_objectSpread({}, option), {}, {
        isDisabled: true
      });
    }

    return option;
  });
};

var createYearOption = function createYearOption() {
  var month = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date();
  var year = month.getFullYear();
  return {
    label: year,
    value: year
  };
};

var NavbarDefault = function NavbarDefault(_ref2) {
  var disabledDaysTimestampMap = _ref2.disabledDaysTimestampMap,
      downcaseMonth = _ref2.downcaseMonth,
      locale = _ref2.locale,
      formatMonthTitle = _ref2.localeUtils.formatMonthTitle,
      month = _ref2.month,
      nextMonth = _ref2.nextMonth,
      monthSelectRef = _ref2.monthSelectRef,
      onChangeMonth = _ref2.onChangeMonth,
      onNextClick = _ref2.onNextClick,
      onPreviousClick = _ref2.onPreviousClick,
      previousMonth = _ref2.previousMonth,
      selectable = _ref2.selectable,
      theme = _ref2.theme,
      type = _ref2.type,
      hideDisabledYears = _ref2.hideDisabledYears,
      yearSelectRef = _ref2.yearSelectRef;
  var initialYear = createYearOption(month);

  var _useState = useState(initialYear),
      _useState2 = _slicedToArray(_useState, 2),
      year = _useState2[0],
      setYear = _useState2[1];

  useEffect(function () {
    if (selectable) {
      setYear(initialYear);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [month, selectable]);
  var monthOptions = useMemo(function () {
    return selectable && getLocale(locale).monthOptions;
  }, [locale, selectable]);
  var currentMonth = useMemo(function () {
    return selectable && monthOptions.find(function (item) {
      return item.value === month.getMonth();
    });
  }, [month, monthOptions, selectable]);
  var months = useMemo(function () {
    return selectable && createMonths({
      disabledDaysTimestampMap: disabledDaysTimestampMap,
      downcaseMonth: downcaseMonth,
      monthOptions: monthOptions,
      year: year
    });
  }, [disabledDaysTimestampMap, downcaseMonth, monthOptions, selectable, year]);
  var years = useMemo(function () {
    var preparedYears = selectable && createYears(disabledDaysTimestampMap, hideDisabledYears);
    return hideDisabledYears ? preparedYears === null || preparedYears === void 0 ? void 0 : preparedYears.filter(function (item) {
      return item.isDisabled !== true;
    }) : preparedYears;
  }, [disabledDaysTimestampMap, selectable, hideDisabledYears]);

  if (selectable) {
    var handleChangeMonth = function handleChangeMonth(selectedMonth) {
      onChangeMonth(new Date(year.value, selectedMonth.value));
    };

    var handleChangeYear = function handleChangeYear(selectedYear) {
      onChangeMonth(new Date(selectedYear.value, currentMonth.value));
    };

    var monthValue = currentMonth && downcaseMonth ? months.find(function (foundMonth) {
      return foundMonth.value === downcaseOption(currentMonth).value;
    }) : months.find(function (foundMonth) {
      return foundMonth.value === currentMonth.value;
    });
    var yearValue = years.find(function (foundYear) {
      return foundYear.value === year.value;
    });

    var selectStyles = function selectStyles(base) {
      return _objectSpread(_objectSpread({}, base), {}, {
        margin: 0
      });
    };

    return /*#__PURE__*/React.createElement("div", {
      className: cx('Navbar', 'Navbar_selection')
    }, /*#__PURE__*/React.createElement("div", {
      className: cx('Navbar_selection_month')
    }, /*#__PURE__*/React.createElement(Select, {
      desktopMaxMenuHeight: 264,
      keepPlaceholder: false,
      menuRef: monthSelectRef,
      onChange: handleChangeMonth,
      options: months,
      size: "small",
      theme: theme,
      type: type,
      value: monthValue,
      placeholder: null,
      styles: {
        input: selectStyles
      },
      shouldScrollIntoView: true,
      autoMobileMenuHeight: true,
      searchable: true
    })), /*#__PURE__*/React.createElement("div", {
      className: cx('Navbar_selection_year')
    }, /*#__PURE__*/React.createElement(Select, {
      desktopMaxMenuHeight: 264,
      keepPlaceholder: false,
      menuRef: yearSelectRef,
      onChange: handleChangeYear,
      options: years,
      rightMenu: type === 'unbordered',
      size: "small",
      theme: theme,
      type: type,
      value: yearValue,
      placeholder: null,
      styles: {
        input: selectStyles
      },
      shouldScrollIntoView: true,
      autoMobileMenuHeight: true // searchable

    })));
  }

  var _getDisabledMinAndMax = getDisabledMinAndMaxTimestam(disabledDaysTimestampMap),
      minDate = _getDisabledMinAndMax.minDate,
      maxDate = _getDisabledMinAndMax.maxDate;

  return /*#__PURE__*/React.createElement("div", {
    className: cx('Navbar')
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: cx('Navbar_button'),
    onClick: function onClick() {
      return onPreviousClick();
    },
    disabled: minDate && minDate >= previousMonth.getTime()
  }, /*#__PURE__*/React.createElement(ChevronNormalLeft, null)), /*#__PURE__*/React.createElement("h6", {
    className: cx('Navbar_title')
  }, formatMonthTitle(month, locale)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: cx('Navbar_button'),
    onClick: function onClick() {
      return onNextClick();
    },
    disabled: maxDate && maxDate < nextMonth.getTime()
  }, /*#__PURE__*/React.createElement(ChevronNormalRight, null)));
};

NavbarDefault.propTypes = process.env.NODE_ENV !== "production" ? {
  onPreviousClick: func,
  onNextClick: func,
  locale: string,
  selectable: bool,
  onChangeMonth: func,
  nextMonth: instanceOf(Date),
  previousMonth: instanceOf(Date),
  // eslint-disable-next-line react/forbid-prop-types
  month: object,
  monthSelectRef: object,
  downcaseMonth: bool,
  disabledDaysTimestampMap: disabledDaysPropType,
  localeUtils: shape({
    formatMonthTitle: func
  }),
  theme: oneOf(themes),
  type: oneOf(navbarTypes),
  hideDisabledYears: bool,
  yearSelectRef: object
} : {};
NavbarDefault.defaultProps = {
  type: 'date',
  downcaseMonth: true
};
export var Navbar = /*#__PURE__*/memo(NavbarDefault);